export const LOCATION_TAGS = [
  'Near transport', 'Near shops', 'Quiet area', 'City centre', 'Garden', 'Pet friendly',
] as const;

export const LEGAL_OWNER_TITLES = [
  'Mr', 'Mrs', 'Ms', 'Miss', 'Mx', 'Dr', 'Lord', 'Lady', 'Sir', 'Dame', 'Company',
] as const;

export type PropertyFormState = {
  address: string;
  postcode: string;
  property_type: string;
  unit_type: string;
  bedrooms: string;
  bathrooms: string;
  status: string;
  monthly_rent: string;
  total_area_sqm: string;
  price_per_sqm: string;
  shared_spaces: string;
  description: string;
  furnished: string;
  floor: string;
  total_floors: string;
  parking: string;
  bills_included: string;
  near_transport: string;
  near_shops: string;
  tags: string[];
  epc_rating: string;
  council_tax_band: string;
  tenure: string;
  hmo_license: string;
  gas_safety_expiry: string;
  eicr_expiry: string;
  insurance_expiry: string;
  ready_to_rent: boolean;
  wheelchair_accessible: string;
  ground_floor: string;
  has_lift: string;
  legal_owner_title: string;
  legal_owner_name: string;
};

export const DEFAULT_PROPERTY_FORM: PropertyFormState = {
  address: '', postcode: '', property_type: 'Terraced House', unit_type: 'residential',
  bedrooms: '3', bathrooms: '1', status: 'to_let', monthly_rent: '', total_area_sqm: '',
  price_per_sqm: '', shared_spaces: '', description: '', furnished: 'no', floor: '',
  total_floors: '', parking: 'no', bills_included: 'no', near_transport: 'no', near_shops: 'no',
  tags: [], epc_rating: '', council_tax_band: '', tenure: 'freehold', hmo_license: '',
  gas_safety_expiry: '', eicr_expiry: '', insurance_expiry: '', ready_to_rent: false,
  wheelchair_accessible: 'no', ground_floor: 'no', has_lift: 'no',
  legal_owner_title: '', legal_owner_name: '',
};

const toDateStr = (v: unknown) => (v ? String(v).split('T')[0] : '');

export function propertyToForm(p: Record<string, any>): PropertyFormState {
  const tags: string[] = [];
  if (p.near_transport === 'yes') tags.push('Near transport');
  if (p.near_shops === 'yes') tags.push('Near shops');
  for (const t of LOCATION_TAGS) {
    if (t !== 'Near transport' && t !== 'Near shops' && p.description?.toLowerCase().includes(t.toLowerCase()) && !tags.includes(t)) {
      tags.push(t);
    }
  }
  return {
    ...DEFAULT_PROPERTY_FORM,
    address: p.address || '',
    postcode: p.postcode || '',
    property_type: p.property_type || 'Terraced House',
    unit_type: p.unit_type || 'residential',
    bedrooms: String(p.bedrooms ?? '3'),
    bathrooms: String(p.bathrooms ?? 1),
    status: p.status || 'to_let',
    monthly_rent: String(p.monthly_rent ?? ''),
    total_area_sqm: p.total_area_sqm ? String(p.total_area_sqm) : '',
    price_per_sqm: p.price_per_sqm ? String(p.price_per_sqm) : '',
    shared_spaces: p.shared_spaces || '',
    description: p.description || '',
    furnished: p.furnished || 'no',
    floor: p.floor != null ? String(p.floor) : '',
    total_floors: p.total_floors != null ? String(p.total_floors) : '',
    parking: p.parking || 'no',
    bills_included: p.bills_included || 'no',
    near_transport: p.near_transport || 'no',
    near_shops: p.near_shops || 'no',
    tags,
    epc_rating: p.epc_rating || '',
    council_tax_band: p.council_tax_band || '',
    tenure: p.tenure || 'freehold',
    hmo_license: p.hmo_license || '',
    gas_safety_expiry: toDateStr(p.gas_safety_expiry),
    eicr_expiry: toDateStr(p.eicr_expiry),
    insurance_expiry: toDateStr(p.insurance_expiry),
    ready_to_rent: !!p.ready_to_rent || p.status === 'ready_to_rent',
    wheelchair_accessible: p.wheelchair_accessible || 'no',
    ground_floor: p.ground_floor || 'no',
    has_lift: p.has_lift || 'no',
    legal_owner_title: p.legal_owner_title || '',
    legal_owner_name: p.legal_owner_name || '',
  };
}

export function formatLegalOwner(title?: string | null, name?: string | null): string {
  const t = (title || '').trim();
  const n = (name || '').trim();
  if (t && n) return `${t} ${n}`;
  return n || t || '';
}

/** Match lease property_address to portfolio property (same rules as backend). */
export function findPropertyByAddress(properties: Array<Record<string, any>>, address: string) {
  if (!address) return undefined;
  const exact = properties.find(p => p.address === address);
  if (exact) return exact;
  const norm = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const n = norm(address);
  return properties.find(p => {
    const pn = norm(p.address);
    return pn === n || pn.includes(n) || n.includes(pn);
  });
}

export function serializePropertyForm(form: PropertyFormState) {
  return {
    ...form,
    bedrooms: Number(form.bedrooms) || 0,
    bathrooms: Number(form.bathrooms) || 1,
    monthly_rent: Number(form.monthly_rent),
    total_area_sqm: form.total_area_sqm ? Number(form.total_area_sqm) : null,
    price_per_sqm: form.price_per_sqm ? Number(form.price_per_sqm) : null,
    floor: form.floor ? Number(form.floor) : null,
    total_floors: form.total_floors ? Number(form.total_floors) : null,
    near_transport: (form.near_transport === 'yes' || form.tags.includes('Near transport')) ? 'yes' : 'no',
    near_shops: (form.near_shops === 'yes' || form.tags.includes('Near shops')) ? 'yes' : 'no',
    ready_to_rent: form.ready_to_rent,
    status: form.ready_to_rent ? 'ready_to_rent' : form.status,
  };
}

export function expiryHint(dateStr: string): { text: string; color: string } | null {
  if (!dateStr) return null;
  const days = Math.floor((new Date(dateStr).getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `Expired ${Math.abs(days)}d ago`, color: '#dc2626' };
  if (days <= 30) return { text: `${days}d left — renew soon`, color: '#d97706' };
  if (days <= 60) return { text: `${days}d left`, color: '#64748b' };
  return { text: `${days}d left`, color: '#16a34a' };
}

export function autoPricePerSqm(rent: string, area: string): string {
  const r = Number(rent);
  const a = Number(area);
  if (r > 0 && a > 0) return (r / a).toFixed(2);
  return '';
}

export function propertyDisplayTags(p: Record<string, any>): string[] {
  const tags: string[] = [];
  if (p.near_transport === 'yes') tags.push('Near transport');
  if (p.near_shops === 'yes') tags.push('Near shops');
  for (const t of LOCATION_TAGS) {
    if (!tags.includes(t) && p.description?.toLowerCase().includes(t.toLowerCase())) tags.push(t);
  }
  return tags;
}
